import type { ApiError, ApiErrorCode, ApiQuery, ApiQueryScalar, ApiResult } from '@/shared/api/types';
import { notifySessionExpired } from '@/shared/auth/session-expiration';
import { isTerminalAuthErrorCode } from '@/shared/auth/session-state';
import { recordReadFailure } from '@/shared/logging/read-failure';

type ApiMethod = 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT';

type ApiRequestOptions = {
  responseType?: 'blob';
  version?: 'V1' | 'V2';
  body?: BodyInit | Record<string, unknown> | null;
  cache?: RequestCache;
  headers?: HeadersInit;
  method?: ApiMethod;
  path: string;
  query?: ApiQuery;
  signal?: AbortSignal;
};

const API_PROXY_PREFIX = '/api/proxy/v1';

type BackendErrorInfo = {
  detail?: string;
  errorCode?: string;
  message?: string;
};

/**
 * 상태 코드 기반으로 공통 에러 코드를 결정합니다.
 */
function mapStatusToErrorCode(status: number): ApiErrorCode {
  if (status === 400) return 'BAD_REQUEST';
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status >= 500) return 'SERVER_ERROR';
  return 'UNKNOWN_ERROR';
}

/**
 * 공통 에러 객체를 생성합니다.
 */
function buildApiError(status: number, message: string, detail?: string, code?: ApiErrorCode, errorCode?: string): ApiError {
  const apiError: ApiError = {
    code: code ?? mapStatusToErrorCode(status),
    detail,
    message,
  };

  if (errorCode) {
    apiError.errorCode = errorCode;
  }

  return apiError;
}

/**
 * HTTP 상태 코드에 따라 사용자에게 보여줄 친화적인 공통 오류 문구를 반환합니다.
 */
function resolveUserFriendlyRequestErrorMessage(status: number): string {
  if (status === 400) {
    return '입력한 내용을 다시 확인해 주세요.';
  }

  if (status === 401) {
    return '로그인이 만료되었습니다. 다시 로그인해 주세요.';
  }

  if (status === 403) {
    return '요청을 처리할 권한이 없습니다.';
  }

  if (status === 404) {
    return '요청한 정보를 찾을 수 없습니다.';
  }

  if (status >= 500) {
    return '일시적인 문제로 요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
  }

  return '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
}

/**
 * 백엔드 에러 문구를 프론트 공통 에러 문구로 정규화합니다.
 */
function resolveApiErrorMessage(status: number, backendMessage?: string): string {
  const normalizedMessage = backendMessage?.trim();
  return normalizedMessage || resolveUserFriendlyRequestErrorMessage(status);
}

/**
 * 사용자 문구와 개발자 상세 문구를 분리한 공통 오류 객체를 생성합니다.
 */
function buildDisplayApiError(status: number, backendError?: string | BackendErrorInfo, code?: ApiErrorCode): ApiError {
  const normalizedError = normalizeBackendErrorInfo(backendError);
  const message = resolveApiErrorMessage(status, normalizedError?.message);
  const detail = normalizedError?.detail?.trim() || message;
  return buildApiError(status, message, detail, code, normalizedError?.errorCode);
}

/**
 * 문자열 또는 구조화된 백엔드 오류 정보를 공통 형태로 정규화합니다.
 */
function normalizeBackendErrorInfo(backendError?: string | BackendErrorInfo): BackendErrorInfo | undefined {
  if (typeof backendError === 'string') {
    const normalizedMessage = backendError.trim();
    return normalizedMessage ? { detail: normalizedMessage, message: normalizedMessage } : undefined;
  }

  return backendError;
}

/**
 * 응답 헤더에서 요청 추적용 request ID를 읽습니다.
 */
function readResponseRequestId(response: Response): string | undefined {
  const requestId = response.headers.get('x-request-id')?.trim();
  return requestId || undefined;
}

/**
 * 브라우저 요청에도 고유 request ID를 부여해 프록시와 백엔드 로그를 연결합니다.
 */
function createClientRequestId(): string {
  return crypto.randomUUID();
}

/**
 * 프록시 경로용 URL을 안전하게 조립합니다.
 */
function buildApiUrl(path: string, query?: ApiQuery, version: 'V1' | 'V2' = 'V1'): string {
  const prefix = version === 'V2' ? '/api/v2' : API_PROXY_PREFIX;
  const normalizedPath = normalizeApiPath(path);
  const searchParams = new URLSearchParams();

  if (query) {
    for (const [key, rawValue] of Object.entries(query)) {
      appendQueryValue(searchParams, key, rawValue);
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `${prefix}/${normalizedPath}?${queryString}` : `${prefix}/${normalizedPath}`;
}

/**
 * 공통 API 클라이언트 입력 경로를 `/api/v1` 기준 상대 경로로 정규화합니다.
 */
function normalizeApiPath(path: string): string {
  return path
    .replace(/^\/+/, '')
    .replace(/^api\/backend\/+/, '')
    .replace(/^api\/v1\/+/, '')
    .replace(/^backend\/+/, '')
    .replace(/^v1\/+/, '');
}

/**
 * 배열/스칼라 쿼리 값을 URLSearchParams에 누적합니다.
 */
function appendQueryValue(searchParams: URLSearchParams, key: string, value: ApiQueryScalar | ApiQueryScalar[]) {
  if (Array.isArray(value)) {
    for (const item of value) {
      appendQueryValue(searchParams, key, item);
    }
    return;
  }

  if (value === null || value === undefined || value === '') {
    return;
  }

  searchParams.append(key, String(value));
}

/**
 * 요청 바디를 JSON 또는 원문 바디로 정규화합니다.
 */
function normalizeRequestBody(body: ApiRequestOptions['body']): { body?: BodyInit; headers?: HeadersInit } {
  if (body === undefined || body === null) {
    return {};
  }

  if (typeof body === 'string' || body instanceof Blob || body instanceof FormData || body instanceof URLSearchParams || body instanceof ArrayBuffer) {
    return { body };
  }

  return {
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  };
}

/**
 * 응답 본문을 JSON 기준으로 안전하게 읽습니다.
 */
async function readJsonResponse(response: Response): Promise<ApiResult<unknown>> {
  const rawText = await response.text();
  const requestId = readResponseRequestId(response);

  if (!rawText) {
    if (response.ok) {
      return { data: null, ok: true, requestId, status: response.status };
    }

    return {
      error: {
        ...buildDisplayApiError(response.status),
        requestId,
      },
      ok: false,
      status: response.status,
    };
  }

  try {
    const parsed = JSON.parse(rawText) as unknown;

    if (response.ok) {
      return { data: parsed, ok: true, requestId, status: response.status };
    }

    const backendError = extractBackendErrorInfo(parsed);
    return {
      error: {
        ...buildDisplayApiError(response.status, backendError),
        requestId,
      },
      ok: false,
      status: response.status,
    };
  } catch {
    if (response.ok) {
      return {
        error: {
          ...buildDisplayApiError(502, '백엔드가 유효한 JSON을 반환하지 않았습니다.', 'INVALID_RESPONSE'),
          requestId,
        },
        ok: false,
        status: 502,
      };
    }

    return {
      error: {
        ...buildDisplayApiError(response.status, rawText),
        requestId,
      },
      ok: false,
      status: response.status,
    };
  }
}

/**
 * 비JSON 응답을 detail 필드 중심으로 표준화합니다.
 */
async function readTextResponse(response: Response): Promise<ApiResult<unknown>> {
  const rawText = await response.text();
  const requestId = readResponseRequestId(response);

  if (response.ok && !rawText) {
    return { data: null, ok: true, requestId, status: response.status };
  }

  if (response.ok) {
    return {
      error: {
        ...buildDisplayApiError(502, '백엔드가 JSON 대신 텍스트를 반환했습니다.', 'INVALID_RESPONSE'),
        requestId,
      },
      ok: false,
      status: 502,
    };
  }

  return {
    error: {
      ...buildDisplayApiError(response.status, rawText),
      requestId,
    },
    ok: false,
    status: response.status,
  };
}

/**
 * 백엔드 에러 응답에서 사용자 문구, 개발자 상세 문구, 오류 코드를 추출합니다.
 */
function extractBackendErrorInfo(payload: unknown): BackendErrorInfo | undefined {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const record = payload as Record<string, unknown>;
  const detailPayload = record.detail;
  let errorCode = readStringField(record, 'error_code');
  let message = readStringField(record, 'message') ?? readStringField(record, 'error_message');
  let detail = readStringField(record, 'error_detail');

  if (typeof detailPayload === 'string') {
    detail = detailPayload.trim() || detail;
  } else if (detailPayload && typeof detailPayload === 'object') {
    const detailRecord = detailPayload as Record<string, unknown>;
    errorCode = errorCode ?? readStringField(detailRecord, 'error_code');
    message = message ?? readStringField(detailRecord, 'message') ?? readStringField(detailRecord, 'error_message');
    detail = detail ?? readStringField(detailRecord, 'detail') ?? readStringField(detailRecord, 'error_detail') ?? message;
  } else if (detailPayload !== undefined && detailPayload !== null) {
    try {
      detail = JSON.stringify(detailPayload);
    } catch {
      detail = String(detailPayload);
    }
  }

  if (!message && detail) {
    message = detail;
  }

  if (!message && !detail && !errorCode) {
    return undefined;
  }

  return { detail, errorCode, message };
}

/**
 * 객체 필드에서 공백이 아닌 문자열 값을 읽습니다.
 */
function readStringField(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim();
  return normalizedValue || undefined;
}

/**
 * 위젯 공통 프록시 API를 호출합니다.
 * 브라우저는 이 함수만 사용하고, 실제 백엔드 인증 전달은 프록시 라우트가 담당합니다.
 */
export async function requestApi<T>(options: ApiRequestOptions): Promise<ApiResult<T>> {
  const method = options.method ?? 'GET';
  const normalizedBody = normalizeRequestBody(options.body);
  const url = buildApiUrl(options.path, options.query, options.version);
  const clientRequestId = createClientRequestId();
  const startedAt = Date.now();

  try {
    const response = await fetch(url, {
      body: method === 'GET' ? undefined : normalizedBody.body,
      cache: options.cache ?? 'no-store',
      credentials: 'include',
      headers: {
        'X-Request-Id': clientRequestId,
        ...(normalizedBody.headers ?? {}),
        ...(options.headers ?? {}),
      },
      method,
      signal: options.signal,
    });

    const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
    const result =
      response.ok && options.responseType === 'blob'
        ? { ok: true as const, data: await response.blob(), status: response.status, requestId: readResponseRequestId(response) }
        : contentType.includes('application/json')
          ? await readJsonResponse(response)
          : await readTextResponse(response);

    /**
     * 프록시가 명시한 terminal 인증 오류만 세션 만료 전역 처리로 전달합니다.
     */
    if (!result.ok && result.status === 401 && isTerminalAuthErrorCode(result.error.errorCode)) {
      notifySessionExpired();
    }

    if (method === 'GET' && !result.ok) {
      recordReadFailure({
        path: url,
        requestId: result.error.requestId ?? clientRequestId,
        status: result.status,
        durationMs: Date.now() - startedAt,
        reason: result.error.code === 'INVALID_RESPONSE' ? 'INVALID_RESPONSE' : 'HTTP_ERROR',
      });
    }

    return result as ApiResult<T>;
  } catch (error) {
    // 사용자 취소는 장애로 기록하지 않고 조회 시간 초과만 통신 진단에 포함합니다.
    const timedOut = options.signal?.reason instanceof Error && options.signal.reason.name === 'TimeoutError';
    if (method === 'GET' && (!options.signal?.aborted || timedOut)) {
      recordReadFailure({ path: url, requestId: clientRequestId, status: 0, durationMs: Date.now() - startedAt, reason: timedOut ? 'TIMEOUT' : 'NETWORK_ERROR' });
    }
    return {
      error: {
        ...buildApiError(0, '네트워크 오류로 요청에 실패했습니다.', error instanceof Error ? error.message : String(error), 'NETWORK_ERROR'),
        requestId: clientRequestId,
      },
      ok: false,
      status: 0,
    };
  }
}

/**
 * GET 전용 래퍼입니다.
 */
export function apiGet<T>(options: Omit<ApiRequestOptions, 'method'>): Promise<ApiResult<T>> {
  return requestApi<T>({ ...options, method: 'GET' });
}

/**
 * POST 전용 래퍼입니다.
 */
export function apiPost<T>(options: Omit<ApiRequestOptions, 'method'>): Promise<ApiResult<T>> {
  return requestApi<T>({ ...options, method: 'POST' });
}

/**
 * PUT 전용 래퍼입니다.
 */
export function apiPut<T>(options: Omit<ApiRequestOptions, 'method'>): Promise<ApiResult<T>> {
  return requestApi<T>({ ...options, method: 'PUT' });
}

/**
 * DELETE 전용 래퍼입니다.
 */
export function apiDelete<T>(options: Omit<ApiRequestOptions, 'method'>): Promise<ApiResult<T>> {
  return requestApi<T>({ ...options, method: 'DELETE' });
}
